import { usePathname, useSegments } from 'expo-router';
import { useEffect, useRef } from 'react';

import { trackScreen, trackStep } from '@/modules/analytics';

/**
 * Turns each route change into one `trackScreen` call on the injected port. Renders nothing.
 *
 * ## Why this lives in `root` and not in `analytics`
 *
 * It is a **bridge between two things, owned by neither**: the router above and the port
 * below. `root` is where composition happens, and it is already the layer that decides
 * whether we own onboarding pageviews at all (`trackScreens`).
 *
 * The placement is load-bearing, not tidiness. This component's `expo-router` import was
 * the ONLY framework dependency in the whole `analytics` module — a module that is otherwise
 * a pure port (`AnalyticsPort`), a facade over it, and a feature-flag hook. Keeping a
 * router edge inside a port module is what made `analytics` unpublishable, and with this file
 * moved its entire external surface is `react` plus `User`. A host implementing
 * `AnalyticsPort` needs to import the type it satisfies; that becomes possible once the two
 * remaining `@/types/user` specifiers in `analytics` become `#types/user`.
 *
 * ## Why it is not a provider
 *
 * It used to be `AnalyticsProvider`, which created no context, rendered `AutoCapture`
 * (returning `null`) beside a `children` pass-through, and so added a tree level to
 * accomplish nothing a sibling could not. `OnboardingRoot` now mounts this next to its
 * content rather than wrapping it. Mounting is the on/off switch — an unmounted component
 * runs no effect — so the `trackScreens` prop resolves at the call site instead of being
 * threaded in and branched on here.
 *
 * Must sit inside the router tree, since it reads `usePathname`; `OnboardingRoot` is itself
 * mounted under the router, so that holds by construction.
 */
type ScreenViewTrackerProps = {
  /**
   * Whether the caller owns the view tree, i.e. `OnboardingRoot`'s `trackScreens`. Gates the
   * VIEW signal only; the step signal fires either way.
   *
   * A prop rather than "mounting is the switch", which is what it used to be. That worked
   * while there was one signal, and stopped working the moment there were two with different
   * owners: an embedded host wants the step and must not have the view.
   */
  ownsViews?: boolean;
};

export const ScreenViewTracker = ({ ownsViews = false }: ScreenViewTrackerProps) => {
  const pathname = usePathname();
  const segments = useSegments();
  const previousPathnameRef = useRef('');

  useEffect(() => {
    if (!pathname) return;

    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;

    const properties = {
      segments: segments.join('/'),
      path: pathname,
    };

    // ONE navigation, TWO signals, and which of them is safe depends on who owns the view
    // tree — see `AnalyticsPort.trackScreen` for the RUM damage the view signal does inside a
    // host, and `trackStep` for the event-shaped alternative that is always safe.
    //
    // Manual rather than PostHog's `captureScreens` — richer properties (segments, path)
    // and no double-tracking against the vendor SDK's own screen capture, which our
    // adapter leaves off for this reason.
    if (ownsViews) {
      trackScreen(pathname, properties);
    }

    // Unconditional: an unimplemented `trackStep` no-ops, and a port that implements it has
    // asked for exactly this. Standalone it is unimplemented on purpose — the view signal
    // above already carries the navigation, and reporting both would count it twice.
    trackStep(pathname, properties);

    // Both usePathname and useSegments derive from the same useRouteInfo() state
    // via useSyncExternalStore, so they always update together in one render.
    // pathname is the sole trigger; segments is read alongside it. `ownsViews` is omitted for
    // the same reason: it comes from `trackScreens`, which is a caller's fixed decision, and
    // including it would re-report the current route if a caller ever toggled it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
};
